/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { MatDialog } from '@angular/material/dialog';
import { Observable, of } from 'rxjs';
import { MyInjector } from '@src/app/app.module';
import { Cycle } from '@src/app/model/cycle';
import { ConfirmDialogService } from '@src/app/shared/dialogs/confirmDialog';
import { ActionColor, IFormAction } from '@src/app/shared/form/form.component';
import { PurchaseDocsComponent } from '@src/app/modules/budget/cycle-page/purchase-docs/purchase-docs.component';
import { PurchaseDocFolder } from '@src/app/modules/budget/cycle-page/purchase-docs/purchaseDocFolder';

/**
 * Collects the supplier invoices, credit notes and cash purchase receipts for a cycle into a
 * folder on the user's machine - the documents an accountant asks for alongside the year end
 * reports.
 *
 * Deliberately not part of the accountants export: those are reports we generate, these are files
 * that already exist. Zipping them onto the cycle would leave us storing a second copy of every
 * invoice, and a third the next time somebody found a gap and ran it again.
 *
 * Writing straight into a chosen folder needs the File System Access API, which only Chromium has,
 * so on Firefox and Safari this says so rather than half working.
 */
export class PurchaseDocsAction implements IFormAction {
    name = $localize`Purchase Documents`;
    color: ActionColor = 'primary';
    show = true;
    icon = 'folder_copy';
    approvalNeeded = false;
    disabled = false;
    approvalText = '';
    tipText = $localize`Save every purchase invoice and receipt for this cycle into a folder`;

    dialog = MyInjector.instance.get(MatDialog);
    cds = MyInjector.instance.get(ConfirmDialogService);

    /**
     * The cycle handed to setup(), kept because the one passed to action() is the form's own
     * version and does not carry teamId - which the folder name needs.
     */
    private cycle: Cycle;

    action(): Observable<Cycle> {
        if (!PurchaseDocFolder.isSupported()) {
            this.showUnsupported();

            return of(null);
        }
        this.dialog.open(PurchaseDocsComponent, { data: { cycle: this.cycle }, minWidth: '500px' });

        return of(null);
    }

    private showUnsupported(): void {
        this.cds.alert($localize`Browser Not Supported`,
            $localize`Saving the purchase documents needs to write a folder of files onto your
            machine, which only Chrome, Edge and Opera can do. Firefox and Safari have no way to
            ask you for a folder, so please open OurOMC in one of those browsers to download them.`);
    }

    setup(cycle: Cycle): void {
        this.cycle = cycle;
        this.show = true;
    }
}
